import axios from "api/axios"
import {
  APIResponse,
  Config,
  ConfigWithURLParams,
  NoContentType,
} from "api/types"
import {
  formatErrorResponse,
  formatSuccessResponse,
  logAPIError,
} from "api/utils"

import { AutoSubmitted } from "../assessments/types"
import {
  CpatAssessment,
  CpatAssessmentViolations,
  CpatAttempt,
  CpatAttemptWarning,
  CpatImageValidation,
  LearningCertificateData,
} from "./types"
import { urls } from "./urls"
export default {
  getAttempt: async ({
    urlParams: { attemptId },
  }: ConfigWithURLParams): Promise<APIResponse<CpatAttempt>> => {
    const url = urls.assessment(attemptId)

    try {
      const res = await axios.post(url)
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  getAssessment: async ({
    params,
  }: Config): Promise<APIResponse<CpatAssessment>> => {
    try {
      const res = await axios.post(urls.getAttempt(params.assessmentId))
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  getAttemptWarning: async ({
    urlParams: { attemptId },
  }: ConfigWithURLParams): Promise<APIResponse<CpatAttemptWarning>> => {
    try {
      const res = await axios.get(urls.getAttemptWarning(attemptId))
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  retrieveProctoringResults: async ({
    urlParams: { attemptId },
  }: ConfigWithURLParams): Promise<APIResponse<CpatAssessmentViolations>> => {
    try {
      const res = await axios.get(urls.retrieveProctoringResults(attemptId))
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  submit: async ({
    urlParams: { attemptId },
  }: ConfigWithURLParams): Promise<APIResponse<NoContentType>> => {
    const url = urls.submitAssessment(attemptId)

    try {
      const res = await axios.post(url)
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  uploadViolation: async ({
    data,
  }: Config): Promise<APIResponse<NoContentType>> => {
    try {
      const res = await axios.post(urls.uploadViolation(), data)
      return formatSuccessResponse(res)
    } catch (e) {
      return formatErrorResponse(e)
    }
  },

  validateImage: async ({
    data,
    ...options
  }: Config): Promise<APIResponse<CpatImageValidation>> => {
    try {
      const res = await axios.post(urls.validateImage(), data, {
        ...options,
        headers: {
          "Content-Type": "multipart/form-data",
          ...options.headers,
        },
      })
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  updateWarningCount: async ({
    urlParams: { attemptId },
    data,
  }: ConfigWithURLParams): Promise<
    APIResponse<NoContentType | AutoSubmitted>
  > => {
    const url = urls.updateWarningCount(attemptId)

    try {
      const res = await axios.post(url, data)
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  markAnswer: async ({
    urlParams: { questionId },
    data,
  }: ConfigWithURLParams): Promise<APIResponse<NoContentType>> => {
    const url = urls.markAnswer(questionId)

    try {
      const res = await axios.put(url, data)
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  getReports: async ({
    queryParams,
  }: {
    queryParams: Record<string, string>
  }): Promise<APIResponse<LearningCertificateData[]>> => {
    const url = urls.getReports()

    try {
      const res = await axios.get(url, { params: queryParams })
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },

  enroll: async ({
    urlParams: { slug },
  }: ConfigWithURLParams): Promise<APIResponse<NoContentType>> => {
    try {
      const res = await axios.post(urls.enrollCpat(slug))
      return formatSuccessResponse(res)
    } catch (e) {
      const error = formatErrorResponse(e)
      logAPIError(error)
      throw error
    }
  },
}
